import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, Input, OnChanges, OnDestroy, signal, SimpleChanges, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { enterLeave } from 'src/app/animations/common.animations';
import { ExpanderGroupService } from 'src/app/components/expander/expander-group.service';
import { ExpanderComponent } from 'src/app/components/expander/expander.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { CheckboxControl } from 'src/app/form-controls/checkbox/checkbox.control';
import { TextAreaControl } from 'src/app/form-controls/textarea/textarea.control';
import { valuesMustMatch } from 'src/app/form-validators/values-must-match.validator';
import { ChangedWidgetValue, DocumentInstanceBuildService, DocumentViewModel } from 'src/app/services/document-instance-build.service';
import { WidgetMetadataMapperService } from 'src/app/services/widget-metadata-mapper.service';
import { setDocumentInstanceWidgetValueRequested, updateSummaryRequested, updateUrgencyRequested } from 'src/app/store/patient-task/patient-task.actions';
import { AppState } from 'src/app/store/reducers';
import { PatientRecordTask, DocumentInstance } from 'src/app/types/aggregate-graph.types';
import { DocumentSectionExpandButtonsComponent } from '../../../document-section-expand-buttons/document-section-expand-buttons.component';
import { DocumentSectionComponent } from '../../../document-section/document-section.component';

interface SummaryFormGroup {
	summary: FormControl<string | null>;
}

interface UrgentFormGroup {
	urgent: FormControl<boolean>;
	urgentReason: FormControl<string>;
}

@Component({
	selector: 'app-task-document',
	templateUrl: './task-document.component.html',
	styleUrl: './task-document.component.scss',
	imports: [
		DocumentSectionExpandButtonsComponent, NgTemplateOutlet, ItemLabelComponent, ExpanderComponent, ReactiveFormsModule,
		TextAreaControl, CheckboxControl, DocumentSectionComponent
	],
	providers: [ExpanderGroupService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [enterLeave]
})
export class TaskDocumentComponent implements OnChanges, OnDestroy {

	@Input() task!: PatientRecordTask;
	@Input() documentInstance!: DocumentInstance;
	@Input() isReadOnly: boolean = true;

	viewModel!: DocumentViewModel;

	summaryForm: FormGroup<SummaryFormGroup> | undefined;
	urgentForm: FormGroup<UrgentFormGroup> | undefined;
	taskPropertiesForm: FormGroup = this.formBuilder.group({});

	private lastChangedWidgetValue: WritableSignal<ChangedWidgetValue> = signal({ documentInstanceId: '', widgetId: 0, value: null });

	constructor(
		private documentInstanceBuildService: DocumentInstanceBuildService, private formBuilder: FormBuilder,
		private store: Store<AppState>, public widgetMetadataMapperService: WidgetMetadataMapperService) {
		effect(() => {
			// effects always run at least once, so guard against saving the initial value
			const value = this.lastChangedWidgetValue();
			if (this.lastChangedWidgetValue().widgetId) this.store.dispatch(setDocumentInstanceWidgetValueRequested(this.lastChangedWidgetValue()));
		});
	}

	private isBuilt: boolean = false;

	ngOnChanges(changes: SimpleChanges) {
		if (!this.isBuilt) {
			this.viewModel = this.documentInstanceBuildService.buildViewModel(this.documentInstance, this.isReadOnly, this.lastChangedWidgetValue);

			if (this.task.manifest.summarySettings.userCanEdit) {

				this.summaryForm = this.formBuilder.group({
					summary: this.formBuilder.control(this.task.summary, { validators: Validators.required })
				});

				if (!this.isReadOnly) {
					this.summaryForm.valueChanges.subscribe(value => {
						this.store.dispatch(updateSummaryRequested({ taskId: this.task.id, summary: value.summary ?? '' }));
					});
				}

				this.addToTaskPropertiesForm('summary', this.summaryForm);
			}

			if (this.task.manifest.canBeUrgent) {

				this.urgentForm = this.formBuilder.nonNullable.group({
					urgent: this.formBuilder.nonNullable.control<boolean>(this.task.isUrgent),
					urgentReason: this.formBuilder.nonNullable.control<string>(this.task.urgentReason)
				}, { validators: valuesMustMatch('urgent', 'urgentReason', false) });

				if (!this.isReadOnly) {
					this.urgentForm.valueChanges.subscribe(value => {
						this.store.dispatch(updateUrgencyRequested({ taskId: this.task.id, urgent: value.urgent ?? false, urgentReason: value.urgentReason ?? '' }));
					});
				}

				this.addToTaskPropertiesForm('urgent', this.urgentForm);
			}

			this.isBuilt = true;
		}
		else if (this.isReadOnly) {
			if (changes['task']) {
				if (this.summaryForm) this.summaryForm.setValue({ summary: this.task.summary });
				if (this.urgentForm) this.urgentForm.setValue({ urgent: this.task.isUrgent, urgentReason: this.task.urgentReason });
			}
			if (changes['documentInstance']) this.documentInstanceBuildService.updateWidgets(this.documentInstance, this.viewModel);
		}
	}

	private addToTaskPropertiesForm(key: string, group: FormGroup) {
		this.taskPropertiesForm.addControl(key, group);
	}

	public documentOrTaskIsInvalid(): boolean {
		return this.viewModel.form.invalid || this.taskPropertiesForm.invalid;
	}

	ngOnDestroy(): void {
		this.viewModel.subscriptionDestroyer.next();
		this.viewModel.subscriptionDestroyer.complete();
	}
}